import type { ChirpStaticEnvironments } from './illustration.types';

/**
 * CDN URL mapping for different environments
 */
const STATIC_ENV_URLS = {
  PRD: 'static.chewy.com',
  STG: 'static-stg.chewy.net',
  QAT: 'static-qat.chewy.net',
  DEV: 'static-dev.chewy.net'
};

/**
 * Base path template for illustrations on the CDN
 */
const getIllustrationBasePath = (version: string) => `/lib/dft/${version}/svg`;

/**
 * Constructs the full CDN URL for an illustration
 * Safely encodes the name parameter to prevent path traversal and injection attacks
 * @param env - The environment (PRD, STG, QAT, DEV)
 * @param name - The illustration name (without .svg extension)
 * @returns The full CDN URL with properly encoded name
 * @throws Error if the name is empty
 */
export function getIllustrationUrl(
  env: ChirpStaticEnvironments,
  name: string,
  version = 'latest'
): string {
  if (!name || name.trim().length === 0) {
    throw new Error('Illustration name cannot be empty');
  }

  const host = STATIC_ENV_URLS[env];
  const basePath = getIllustrationBasePath(version);
  // Use encodeURIComponent to safely encode the name for use in URLs
  // This prevents path traversal and special character injection
  const encodedName = encodeURIComponent(name);
  return `https://${host}${basePath}/${encodedName}.svg`;
}

/**
 * Parses SVG text and extracts the <svg> element
 * Uses DOMParser to safely parse the SVG content
 * @param svgText - The raw SVG text content
 * @returns The parsed SVG element or null if parsing fails
 */
export function parseSvgFromString(svgText: string): SVGSVGElement | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgText, 'text/html');
  return doc.querySelector('svg');
}

/**
 * Copies attributes and children from source SVG to target SVG element
 * Only copies attributes that don't already exist on the target to preserve component props.
 * @param source - The source SVG element (fetched from CDN)
 * @param target - The target SVG element (rendered in React)
 */
export function copySvgContents(source: SVGSVGElement, target: SVGSVGElement): void {
  // Copy attributes from source, but don't override existing attributes on target
  // This preserves props like width/height passed to the component
  Array.from(source.attributes).forEach(attr => {
    if (!target.hasAttribute(attr.name)) {
      target.setAttribute(attr.name, attr.value);
    }
  });

  // Copy all children (paths, groups, etc.)
  target.innerHTML = source.innerHTML;
}

/**
 * Validates that the fetch response is valid SVG content
 * @param response - The fetch response
 * @throws Error if response is not OK or content-type is not SVG
 */
export async function validateSvgResponse(response: Response): Promise<void> {
  if (!response.ok) {
    throw new Error(`Failed to fetch illustration: ${response.status} ${response.statusText}`);
  }

  const contentType = response.headers.get('content-type');
  if (contentType && !contentType.includes('svg')) {
    throw new Error(`Invalid content type: expected SVG, got ${contentType}`);
  }
}

/**
 * Validates accessibility props in development mode
 * Throws an error if an informative illustration is missing aria-label
 * or if a decorative illustration has an aria-label
 * @param isDecorative - Whether the illustration is decorative
 * @param ariaLabel - The aria-label value
 */
export function validateAccessibility(
  isDecorative: boolean | undefined,
  ariaLabel: string | undefined
): void {
  if (process.env.NODE_ENV === 'development') {
    if (!isDecorative && !ariaLabel) {
      throw new Error(
        'ChirpIllustration: aria-label is required when isDecorative is false. ' +
          'Either set isDecorative={true} or provide an aria-label.'
      );
    }
    if (isDecorative && ariaLabel) {
      throw new Error(
        'ChirpIllustration: Decorative illustrations should not have an aria-label. ' +
          'Remove the aria-label prop or set isDecorative={false}.'
      );
    }
  }
}

/**
 * Dispatches a native error event on the SVG element
 * @param svgElement - The SVG element to dispatch the error on
 * @param error - The error that occurred
 */
export function dispatchErrorEvent(svgElement: SVGSVGElement, error: unknown): void {
  const err = error instanceof Error ? error : new Error(String(error));
  const errorEvent = new ErrorEvent('error', {
    error: err,
    message: err.message
  });
  svgElement.dispatchEvent(errorEvent);
}

/**
 * Returns accessibility props based on whether the illustration is decorative
 * @param isDecorative - Whether the illustration is decorative
 * @param ariaLabel - The aria-label for informative illustrations
 * @returns Accessibility props object
 */
export function getAccessibilityProps(isDecorative: boolean, ariaLabel: string | undefined) {
  return isDecorative
    ? { 'aria-hidden': 'true' as const }
    : {
        role: 'img' as const,
        'aria-label': ariaLabel
      };
}

/**
 * Loads an illustration from the CDN and injects it into the SVG element
 * @param options - Loading options
 * @returns Promise that resolves when loading is complete
 */
export async function loadIllustration(options: {
  svgElement: SVGSVGElement;
  name: string;
  env: ChirpStaticEnvironments;
  version?: string;
  onLoad?: () => void;
  onError?: (error: unknown) => void;
}): Promise<void> {
  const { svgElement, name, env, version = 'latest', onLoad, onError } = options;

  try {
    const url = getIllustrationUrl(env, name, version);
    const response = await fetch(url);

    // Validate response
    await validateSvgResponse(response);

    // Parse SVG content
    const svgText = await response.text();
    const sourceSvg = parseSvgFromString(svgText);

    if (!sourceSvg) {
      throw new Error('Failed to parse SVG content');
    }

    // Update DOM
    copySvgContents(sourceSvg, svgElement);

    // Call success callback
    onLoad?.();
  } catch (error) {
    // Dispatch error event for native error handling
    dispatchErrorEvent(svgElement, error);

    // Call error callback if provided
    onError?.(error);

    // Don't throw - let the component render empty (graceful degradation)
    // This matches the behavior of <img> tags when they fail to load
  }
}
